import { decryptAtRest, encryptAtRest } from "./crypto";
import { getDb, type ImageAtRestCacheRow } from "./db";
import {
  getAttachmentCacheMaxEntryBytes,
  getAttachmentCacheMaxTotalBytes,
} from "./attachmentCacheSettings";
import { getLocalCacheKey } from "./localCacheKey";

/** @deprecated Используй getAttachmentCacheMaxTotalBytes() — лимит задаётся в настройках. */
export const CHAT_IMAGE_CACHE_MAX_BYTES = 200 * 1024 * 1024;

async function evictImageCacheIfNeeded(neededBytes: number): Promise<void> {
  const maxTotal = getAttachmentCacheMaxTotalBytes();
  if (neededBytes > maxTotal) {
    return;
  }
  const db = await getDb();
  const rows = await db.getAll("image_atr_cache");
  let total = 0;
  for (const row of rows) {
    total += row.byteLength;
  }
  if (total + neededBytes <= maxTotal) {
    return;
  }

  const rw = db.transaction("image_atr_cache", "readwrite");
  const storeRw = rw.objectStore("image_atr_cache");
  const idx = storeRw.index("by-lru");
  let cursor = await idx.openCursor();
  while (total + neededBytes > maxTotal && cursor) {
    const row = cursor.value as ImageAtRestCacheRow;
    total -= row.byteLength;
    await cursor.delete();
    cursor = await cursor.continue();
  }
  await rw.done;
}

/** Удалить записи крупнее лимита и ужать общий размер до настроенного потолка. */
export async function enforceAttachmentCacheBudget(): Promise<void> {
  if (!getLocalCacheKey()) return;
  const maxEntry = getAttachmentCacheMaxEntryBytes();
  const maxTotal = getAttachmentCacheMaxTotalBytes();
  const db = await getDb();
  const rows = await db.getAll("image_atr_cache");
  const rw = db.transaction("image_atr_cache", "readwrite");
  const store = rw.objectStore("image_atr_cache");
  for (const row of rows) {
    if (row.byteLength > maxEntry) {
      await store.delete(row.blobId);
    }
  }
  await rw.done;

  let total = 0;
  const rest = await db.getAll("image_atr_cache");
  for (const row of rest) {
    total += row.byteLength;
  }
  if (total <= maxTotal) return;

  const rw2 = db.transaction("image_atr_cache", "readwrite");
  const store2 = rw2.objectStore("image_atr_cache");
  const idx2 = store2.index("by-lru");
  let cursor = await idx2.openCursor();
  while (total > maxTotal && cursor) {
    const row = cursor.value as ImageAtRestCacheRow;
    total -= row.byteLength;
    await cursor.delete();
    cursor = await cursor.continue();
  }
  await rw2.done;
}

export async function readChatImageCached(blobId: string): Promise<Uint8Array | null> {
  const key = getLocalCacheKey();
  if (!key) return null;
  const db = await getDb();
  const row = await db.get("image_atr_cache", blobId);
  if (!row) return null;
  try {
    const encrypted = new Uint8Array(row.encrypted);
    const plain = decryptAtRest(encrypted, key);
    await db.put("image_atr_cache", {
      ...row,
      lastAccessAt: Date.now(),
    });
    return plain;
  } catch {
    await db.delete("image_atr_cache", blobId);
    return null;
  }
}

export async function writeChatImageCached(blobId: string, mime: string, plainBytes: Uint8Array): Promise<void> {
  const key = getLocalCacheKey();
  if (!key) return;
  const byteLength = plainBytes.byteLength;
  const maxEntry = getAttachmentCacheMaxEntryBytes();
  const maxTotal = getAttachmentCacheMaxTotalBytes();
  if (byteLength > maxEntry) {
    return;
  }
  if (byteLength > maxTotal) {
    return;
  }
  await evictImageCacheIfNeeded(byteLength);
  const encrypted = encryptAtRest(plainBytes, key);
  const db = await getDb();
  await db.put("image_atr_cache", {
    blobId,
    mime,
    encrypted: encrypted.slice().buffer,
    byteLength,
    lastAccessAt: Date.now(),
  });
}
